import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Label } from '@c2fo/liquidity';
import FilterDrawer from '@/enterprise/components/FilterDrawer';
import { IneligibleInvoiceFetchFilters, IneligibleInvoiceFetchOptions } from '@/enterprise/data/useIneligibleInvoices';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useLocaleFormat from '@/utils/useLocaleFormat';

const IneligibleInvoiceFilters = ({
  filterOpen,
  handleFilter,
  setFilterOpen,
  takerMarket,
  urlState,
}: {
  filterOpen: boolean;
  handleFilter: ({ key, value }: { key: keyof IneligibleInvoiceFetchFilters; value: string }) => void;
  setFilterOpen: (open: boolean) => void;
  takerMarket: TakerMarket;
  urlState: Partial<IneligibleInvoiceFetchOptions>;
}) => {
  const { t } = useTranslation();
  const { asCurrency } = useLocaleFormat();

  return (
    <FilterDrawer filterOpen={filterOpen} setFilterOpen={setFilterOpen}>
      <div className="space-y-4 p-4">
        <div className="w-full space-y-1">
          <Label>{t('core.status')}</Label>
          <div className="space-y-2 rounded border border-stroke bg-white p-4">
            <Checkbox
              checked={(urlState?.filter?.status ?? []).includes('isAdjusted')}
              color="secondary"
              id="isAdjusted"
              label={t('taker.invoiceStatus.isAdjusted')}
              onChange={() => handleFilter({ key: 'status', value: 'isAdjusted' })}
              value="isAdjusted"
            />
            <Checkbox
              checked={(urlState?.filter?.status ?? []).includes('isPastDue')}
              color="secondary"
              id="isPastDue"
              label={t('taker.invoiceStatus.isPastDue')}
              onChange={() => handleFilter({ key: 'status', value: 'isPastDue' })}
              value="isPastDue"
            />
            <Checkbox
              checked={(urlState?.filter?.status ?? []).includes('isMakerExcluded')}
              color="secondary"
              id="isMakerExcluded"
              label={t('taker.invoiceStatus.isMakerExcluded')}
              onChange={() => handleFilter({ key: 'status', value: 'isMakerExcluded' })}
              value="isMakerExcluded"
            />
          </div>
        </div>
        <div className="w-full space-y-1">
          <Label>{t('core.invoiceAmount')}</Label>
          <div className="space-y-2 rounded border border-stroke bg-white p-4">
            <Checkbox
              checked={(urlState?.filter?.amount ?? []).includes(',25000')}
              color="secondary"
              id=",25000"
              label={t('core.rangeFilterLowest', { max: asCurrency(25000, takerMarket.currency) })}
              onChange={() => handleFilter({ key: 'amount', value: ',25000' })}
              value=",25000"
            />
            <Checkbox
              checked={(urlState?.filter?.amount ?? []).includes('25000,50000')}
              color="secondary"
              id="25000,50000"
              label={t('core.rangeFilterMiddle', {
                min: asCurrency(25000, takerMarket.currency),
                max: asCurrency(50000, takerMarket.currency),
              })}
              onChange={() => handleFilter({ key: 'amount', value: '25000,50000' })}
              value="25000,50000"
            />
            <Checkbox
              checked={(urlState?.filter?.amount ?? []).includes('50000,75000')}
              color="secondary"
              id="50000,75000"
              label={t('core.rangeFilterMiddle', {
                min: asCurrency(50000, takerMarket.currency),
                max: asCurrency(75000, takerMarket.currency),
              })}
              onChange={() => handleFilter({ key: 'amount', value: '50000,75000' })}
              value="50000,75000"
            />
            <Checkbox
              checked={(urlState?.filter?.amount ?? []).includes('75000,')}
              color="secondary"
              id="75000,"
              label={t('core.rangeFilterHighest', { min: asCurrency(75000, takerMarket.currency) })}
              onChange={() => handleFilter({ key: 'amount', value: '75000,' })}
              value="75000,"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end px-4 pb-4">
        <Button onClick={() => setFilterOpen(false)} variant="secondary">
          {t('core.close')}
        </Button>
      </div>
    </FilterDrawer>
  );
};

export default IneligibleInvoiceFilters;
